import { ASANA_CUSTOM_FIELDS } from '../constants';

export interface AsanaTask {
  name: string;
  gid: string;
  custom_fields?: Array<{
    name: string;
    display_value?: string;
    text_value?: string;
    enum_value?: {
      name: string;
    };
  }>;
}

export interface CustomerInfo {
  design_date: string;
  customer_name: string;
  street_number: string;
  street_name: string;
  city: string;
  state_short_name: string;
  zip_code: string;
  utility_name: string;
  utility_meter_number: string;
  ahj_name: string;
  texas_esi_id: string;
  parcel_number_apn: string;
}

/**
 * Extracts a custom field value from an Asana task
 */
const getCustomFieldValue = (task: AsanaTask, fieldName: string): string => {
  const field = task.custom_fields?.find((cf) => cf.name === fieldName);
  return field?.display_value || field?.text_value || field?.enum_value?.name || '';
};

/**
 * Extracts a custom field value from an Asana task, checking multiple possible field names
 */
const getCustomFieldValueMultiple = (task: AsanaTask, fieldNames: readonly string[]): string => {
  for (const fieldName of fieldNames) {
    const value = getCustomFieldValue(task, fieldName);
    if (value) {
      return value;
    }
  }
  return '';
};

/**
 * Parses a full street address into number and name
 * Example: "123 Main Street" -> { number: "123", name: "Main Street" }
 */
const parseStreetAddress = (fullStreet: string): { number: string; name: string } => {
  if (!fullStreet) {
    return { number: '', name: '' };
  }

  // Match pattern: one or more digits/letters followed by optional letter (like 123A)
  // followed by whitespace, then the rest is the street name
  const match = fullStreet.trim().match(/^([0-9]+[A-Za-z]?)\s+(.+)$/);
  
  if (match) {
    return {
      number: match[1],
      name: match[2]
    };
  }
  
  // If no match, assume entire string is street name
  return { number: '', name: fullStreet };
};

/**
 * Extracts the Client custom field value from a task (Asana or ClickUp).
 * Checks multiple field name variants:
 *   Asana: "Client", "Client:"
 *   ClickUp: "[GEN] Client"
 */
export const extractClientFromTask = (task: AsanaTask): string =>
  getCustomFieldValueMultiple(task, [...ASANA_CUSTOM_FIELDS.CLIENT_NAMES, ASANA_CUSTOM_FIELDS.CLICKUP_CLIENT]);

/**
 * Extracts customer information from an Asana task
 */
export const extractCustomerInfoFromTask = (task: AsanaTask): Partial<CustomerInfo> => {
  const fullStreet = getCustomFieldValue(task, ASANA_CUSTOM_FIELDS.STREET_NAME);
  const { number: streetNumber, name: streetName } = parseStreetAddress(fullStreet);
  
  return {
    design_date: new Date().toISOString().split('T')[0],
    customer_name: task.name || '',
    street_number: streetNumber,
    street_name: streetName,
    city: getCustomFieldValue(task, ASANA_CUSTOM_FIELDS.CITY),
    state_short_name: getCustomFieldValue(task, ASANA_CUSTOM_FIELDS.STATE),
    zip_code: getCustomFieldValue(task, ASANA_CUSTOM_FIELDS.ZIP_CODE),
    utility_name: getCustomFieldValue(task, ASANA_CUSTOM_FIELDS.UTILITY_NAME),
    ahj_name: getCustomFieldValueMultiple(task, ASANA_CUSTOM_FIELDS.AHJ_NAMES)
  };
};